import { useState, useEffect, useRef } from "react";
import {
  collection, getDocs, getDoc, doc, addDoc, setDoc, updateDoc,
  deleteDoc, serverTimestamp, query, orderBy
} from "firebase/firestore";
import { db } from "../../firebase/config";
import {
  calcImportacion, calcVenta, precioSugerido,
  analisisVsMercado, GLOBALES_DEFAULT, NCM_POR_RUBRO,
  fmtUSD, fmtARS, fmtPct, getNcmPorRubro, CBM_REFERENCIA
} from "../../utils/ddp";
import { sanitizeForFirestore } from "../../utils/firestore";

const G = {
  bg:"#FAF9F7",s1:"#FFFFFF",s2:"#F4F2EF",border:"#E2DFD9",border2:"#D4D0CA",
  black:"#1C1917",off:"#3D3830",muted:"#8C8078",dim:"#C8C0B8",
  orange:"#D2611A",green:"#16A34A",red:"#DC2626",amber:"#D97706",blue:"#2563EB",
  sans:"'Outfit','Helvetica Neue',Arial,sans-serif",
  mono:"'IBM Plex Mono',monospace",
};
const O = "#D2611A", BK = "#1C1917";

const RUBROS = ["Sanitarios","Bañeras","Vanitorys","Pisos SPC","Hornos y Anafes","Puertas","Carpintería","Cocinas","Barandas y Balcones"];
const UNIDADES = ["u","m²","m","kg","par","set","kit"];

// ── Uploader de imagen a Cloudinary ──────────────────────────────────────────
function ImageUploader({ value, onChange, label="Imagen principal" }) {
  const [uploading, setUploading] = useState(false);
  const ref = useRef();

  const upload = async (file) => {
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("upload_preset", "ml_default");
    try {
      const res = await fetch("https://api.cloudinary.com/v1_1/dw5tahkhi/image/upload", { method:"POST", body:fd });
      const data = await res.json();
      onChange(data.secure_url);
    } catch(e) { console.error(e); }
    setUploading(false);
  };

  return (
    <div>
      <div style={{ fontFamily:G.sans, fontSize:9, letterSpacing:2, textTransform:"uppercase", color:G.muted, marginBottom:5, fontWeight:600 }}>{label}</div>
      <div style={{ display:"flex", gap:10, alignItems:"flex-start" }}>
        {value && (
          <div style={{ position:"relative" }}>
            <img src={value} alt="" style={{ width:80, height:80, objectFit:"contain", background:G.s2, border:`1px solid ${G.border}`, borderRadius:2 }}/>
            <button onClick={()=>onChange("")} style={{ position:"absolute", top:-6, right:-6, width:18, height:18, background:G.red, color:"#fff", border:"none", borderRadius:"50%", cursor:"pointer", fontSize:10, lineHeight:1 }}>×</button>
          </div>
        )}
        <div>
          <input type="file" accept="image/*" ref={ref} style={{ display:"none" }} onChange={e=>e.target.files[0]&&upload(e.target.files[0])}/>
          <button onClick={()=>ref.current.click()} disabled={uploading}
            style={{ padding:"7px 14px", background:G.s2, border:`1px solid ${G.border}`, fontFamily:G.sans, fontSize:9, letterSpacing:1.5, textTransform:"uppercase", color:G.off, cursor:"pointer", fontWeight:600 }}>
            {uploading ? "Subiendo..." : value ? "Cambiar imagen" : "+ Subir imagen"}
          </button>
          {value && <div style={{ fontFamily:G.mono, fontSize:8, color:G.muted, marginTop:4, wordBreak:"break-all", maxWidth:200 }}>{value.slice(-40)}</div>}
        </div>
      </div>
    </div>
  );
}

// ── Uploader adicional para galería ──────────────────────────────────────────
function ImageUploaderMulti({ onAdd }) {
  const [uploading, setUploading] = useState(false);
  const ref = useRef();

  const upload = async (file) => {
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("upload_preset", "ml_default");
    try {
      const res = await fetch("https://api.cloudinary.com/v1_1/dw5tahkhi/image/upload", { method:"POST", body:fd });
      const data = await res.json();
      if (data.secure_url) onAdd(data.secure_url);
    } catch(e) { console.error(e); }
    setUploading(false);
  };

  return (
    <div>
      <input type="file" accept="image/*" ref={ref} style={{ display:"none" }} onChange={e=>e.target.files[0]&&upload(e.target.files[0])}/>
      <button onClick={()=>ref.current.click()} disabled={uploading}
        style={{ width:72, height:72, background:G.s2, border:`2px dashed ${G.border2}`, cursor:"pointer", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:4, color:G.muted, borderRadius:2 }}>
        <span style={{ fontSize:20, lineHeight:1 }}>{uploading?"…":"+"}</span>
        <span style={{ fontFamily:G.sans, fontSize:8, letterSpacing:1 }}>Agregar</span>
      </button>
    </div>
  );
}

// ── Modal de producto ─────────────────────────────────────────────────────────
function ModalProducto({ producto, globales, proveedores, onGuardar, onClose }) {
  const esNuevo = !producto;
  const [tab, setTab] = useState("info");

  // IMPORTANTE: el form nunca debe tener el campo id
  // El id del producto se usa SOLO via el prop `producto.id` al momento de guardar
  const [form, setForm] = useState(() => {
    if (!producto) return {
      nombre:"", sku:"", rubro:"Sanitarios", unidad:"u",
      descripcion:"", descripcion_corta:"", caracteristicas:"",
      imagen_url:"", imagenes:[],
      fob_compra:"", fob_declarado:"", cbm:"",
      precio_venta:"", precio_con_iva:"",
      publicado:true, orden:10,
      proveedor_id:"", proveedor_nombre:"",
    };
    // Excluir id del form para evitar que se guarde en Firestore o se copie al duplicar
    const { id: _id, ...resto } = producto;
    return {
      ...resto,
      imagenes: [...(producto.imagenes || [])], // copia del array
    };
  });
  const [saving, setSaving] = useState(false);
  const [mlUrl, setMlUrl] = useState("");
  const [mlLoading, setMlLoading] = useState(false);
  const [resultado, setResultado] = useState(null);

  const set = (k) => (v) => setForm(p => ({ ...p, [k]: v }));
  const g = globales || GLOBALES_DEFAULT;

  // Calcular DDP en tiempo real
  useEffect(() => {
    if (form.fob_compra && form.cbm) {
      const r = calcImportacion({ fob_compra:form.fob_compra, fob_declarado:form.fob_declarado||form.fob_compra, cbm:form.cbm, rubro:form.rubro, globales:g });
      setResultado(r);
    } else setResultado(null);
  }, [form.fob_compra, form.fob_declarado, form.cbm, form.rubro, globales]);

  // Importar desde ML
  const importarML = async () => {
    if (!mlUrl.includes("mercadolibre")) return;
    setMlLoading(true);
    try {
      const res = await fetch(`/api/ml?url=${encodeURIComponent(mlUrl)}`);
      const data = await res.json();
      if (data.nombre) setForm(p => ({ ...p, nombre:data.nombre||p.nombre, descripcion:data.descripcion||p.descripcion, imagen_url:data.imagen_url||p.imagen_url, caracteristicas:data.caracteristicas||p.caracteristicas }));
    } catch(e) { console.error(e); }
    setMlLoading(false);
  };

  const guardar = async () => {
    if (!form.nombre.trim()) return;
    console.log("%c[GUARDAR] click", "color:#2563EB;font-weight:bold", { esNuevo, productoId: producto?.id, productoNombreOriginal: producto?.nombre, formNombreAEnviar: form.nombre });
    setSaving(true);
    let ok = false;
    try {
      const base = sanitizeForFirestore({
        ...form,
        precio_venta:   parseFloat(form.precio_venta)   || null,
        precio_con_iva: parseFloat(form.precio_con_iva) || null,
        fob_compra:     parseFloat(form.fob_compra)     || null,
        fob_declarado:  parseFloat(form.fob_declarado)  || null,
        cbm:            parseFloat(form.cbm)            || null,
        orden:          parseInt(form.orden)             || 10,
        imagenes:       [...(form.imagenes || [])],
        updatedAt:      serverTimestamp(),
      });
      if (esNuevo) {
        base.creadoEn = serverTimestamp();
        const ref = await addDoc(collection(db, "productos"), base);
        console.log("%c[GUARDAR] creó documento NUEVO (addDoc)", "color:#DC2626;font-weight:bold", { nuevoId: ref.id, nombre: base.nombre });
      } else {
        // Verificar que el documento todavía exista antes de actualizar. Si ya no existe
        // (p.ej. se borró desde otra pestaña/sesión), avisar explícitamente en vez de
        // recrearlo en silencio con setDoc+merge.
        const snapActual = await getDoc(doc(db, "productos", producto.id));
        if (!snapActual.exists()) {
          const recrear = window.confirm(
            `El producto "${producto.nombre}" ya no existe en la base de datos (probablemente fue borrado).\n\n¿Querés crearlo de nuevo con los datos actuales? Si cancelás, no se guarda nada.`
          );
          if (!recrear) { setSaving(false); return; }
          base.creadoEn = serverTimestamp();
          const ref = await addDoc(collection(db, "productos"), base);
          console.log("%c[GUARDAR] el documento no existía — se recreó como NUEVO", "color:#DC2626;font-weight:bold", { nuevoId: ref.id, idViejo: producto.id, nombre: base.nombre });
        } else {
          console.log("%c[GUARDAR] actualiza documento EXISTENTE (setDoc merge)", "color:#16A34A;font-weight:bold", { id: producto.id, nombre: base.nombre });
          await setDoc(doc(db, "productos", producto.id), base, { merge: true });
        }
      }
      ok = true;
    } catch(e) {
      console.error("Error guardando:", e);
      alert("Error al guardar: " + e.message);
    } finally {
      setSaving(false);   // siempre se ejecuta, pase lo que pase
    }
    if (ok) onGuardar(); // solo cerrar modal si guardó bien
  };

  const duplicar = async () => {
    console.log("%c[DUPLICAR] click", "color:#D97706;font-weight:bold", { productoId: producto?.id, productoNombre: producto?.nombre });
    setSaving(true);
    let ok = false;
    try {
      const copiaPura = JSON.parse(JSON.stringify(form));
      delete copiaPura.id;
      const copia = sanitizeForFirestore({
        ...copiaPura,
        nombre:    form.nombre + " (copia)",
        imagenes:  JSON.parse(JSON.stringify(form.imagenes || [])),
        publicado: false,
        creadoEn:  serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      const ref = await addDoc(collection(db, "productos"), copia);
      console.log("✓ Duplicado ID:", ref.id, "| Original ID:", producto?.id);
      ok = true;
    } catch(e) {
      console.error("Error duplicando:", e);
      alert("Error al duplicar: " + e.message);
    } finally {
      setSaving(false);
    }
    if (ok) onGuardar();
  };

  const inp = { width:"100%", background:"transparent", border:"none", borderBottom:`1.5px solid ${G.border2}`, color:G.black, fontFamily:G.sans, fontSize:13, padding:"7px 0", outline:"none" };
  const lbl = { fontFamily:G.sans, fontSize:9, letterSpacing:2, textTransform:"uppercase", color:G.muted, marginBottom:5, fontWeight:600, display:"block" };
  const sel = { ...inp, background:G.s2, border:`1px solid ${G.border}`, padding:"7px 10px", WebkitAppearance:"menulist" };

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(28,25,23,.5)", zIndex:300, display:"flex", alignItems:"flex-start", justifyContent:"center", overflow:"auto", padding:"20px 10px" }}>
      <div style={{ background:G.s1, border:`1.5px solid ${G.border}`, width:"100%", maxWidth:720 }}>

        {/* Header */}
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"18px 24px", borderBottom:`1px solid ${G.border}` }}>
          <div>
            <div style={{ fontFamily:G.sans, fontSize:18, color:G.black, fontWeight:700 }}>{esNuevo ? "Nuevo producto" : "Editar producto"}</div>
            {!esNuevo && <div style={{ fontFamily:G.mono, fontSize:9, color:G.dim, marginTop:2 }}>ID: {producto.id}</div>}
          </div>
          <button onClick={onClose} style={{ background:"none", border:"none", fontSize:22, color:G.muted, cursor:"pointer" }}>×</button>
        </div>

        {/* Tabs */}
        <div style={{ display:"flex", borderBottom:`1px solid ${G.border}` }}>
          {[["info","Información"],["calc","Calculadora DDP"]].map(([t,l]) => (
            <button key={t} onClick={() => setTab(t)}
              style={{ padding:"11px 22px", fontFamily:G.sans, fontSize:9, letterSpacing:2, textTransform:"uppercase", fontWeight:tab===t?700:500, color:tab===t?O:G.muted, background:"transparent", border:"none", borderBottom:tab===t?`2px solid ${O}`:"2px solid transparent", cursor:"pointer", marginBottom:-1 }}>
              {l}
            </button>
          ))}
        </div>

        <div style={{ padding:"22px 24px" }}>
          {tab === "info" && (
            <>
              {/* Importar ML */}
              <div style={{ background:G.s2, border:`1px solid ${G.border}`, padding:"12px 16px", marginBottom:20 }}>
                <div style={{ fontFamily:G.sans, fontSize:9, letterSpacing:2, textTransform:"uppercase", color:O, marginBottom:8, fontWeight:700 }}>✦ Importar desde MercadoLibre</div>
                <div style={{ display:"flex", gap:8 }}>
                  <input value={mlUrl} onChange={e=>setMlUrl(e.target.value)} placeholder="https://articulo.mercadolibre.com.ar/MLA-..."
                    style={{ flex:1, background:G.s1, border:`1px solid ${G.border}`, color:G.black, fontFamily:G.sans, fontSize:12, padding:"7px 10px", outline:"none" }}/>
                  <button onClick={importarML} disabled={mlLoading}
                    style={{ padding:"7px 16px", background:O, color:"#fff", fontFamily:G.sans, fontSize:9, letterSpacing:2, textTransform:"uppercase", border:"none", cursor:"pointer", fontWeight:700 }}>
                    {mlLoading ? "..." : "Importar →"}
                  </button>
                </div>
                <div style={{ fontFamily:G.sans, fontSize:10, color:G.muted, marginTop:5 }}>Pre-llena nombre, descripción, imagen y especificaciones</div>
              </div>

              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, marginBottom:14 }}>
                <div style={{ gridColumn:"1/-1" }}>
                  <label style={lbl}>Nombre del producto *</label>
                  <input value={form.nombre} onChange={e=>set("nombre")(e.target.value)} placeholder="Ej: Inodoro largo rimless blanco" style={inp}/>
                </div>
                <div>
                  <label style={lbl}>SKU</label>
                  <input value={form.sku||""} onChange={e=>set("sku")(e.target.value)} placeholder="WA-SAN001" style={inp}/>
                </div>
                <div>
                  <label style={lbl}>Unidad</label>
                  <select value={form.unidad||"u"} onChange={e=>set("unidad")(e.target.value)} style={{...sel,color:G.black,background:"#FFFFFF"}}>
                    {UNIDADES.map(u=><option key={u}>{u}</option>)}
                  </select>
                </div>
                <div>
                  <label style={lbl}>Rubro</label>
                  <select value={form.rubro||"Sanitarios"} onChange={e=>set("rubro")(e.target.value)} style={{...sel,color:G.black,background:"#FFFFFF"}}>
                    {RUBROS.map(r=><option key={r}>{r}</option>)}
                  </select>
                </div>
                <div>
                  <label style={lbl}>Orden en catálogo</label>
                  <input type="number" value={form.orden||""} onChange={e=>set("orden")(parseInt(e.target.value)||10)} placeholder="10" style={inp}/>
                  <div style={{ fontFamily:G.mono, fontSize:8, color:G.muted, marginTop:2 }}>Menor número = aparece primero</div>
                </div>
              </div>

              {/* Proveedor — INTERNO, no visible al cliente */}
              <div style={{ background:"#EFF6FF", border:"1px solid #2563EB33", padding:"12px 16px", marginBottom:16 }}>
                <div style={{ fontFamily:G.sans, fontSize:9, letterSpacing:2, textTransform:"uppercase", color:"#2563EB", marginBottom:8, fontWeight:700 }}>🔒 Proveedor (interno — no visible al cliente)</div>
                <select value={form.proveedor_id||""} onChange={e=>{
                  const p = proveedores.find(x=>x.id===e.target.value);
                  setForm(f=>({...f, proveedor_id:e.target.value, proveedor_nombre:p?.nombre||""}));
                }} style={{ width:"100%", background:"#FFFFFF", color:G.black, border:`1px solid #2563EB44`, fontFamily:G.sans, fontSize:13, padding:"7px 10px", outline:"none", WebkitAppearance:"menulist" }}>
                  <option value="">Sin asignar</option>
                  {proveedores.map(p=><option key={p.id} value={p.id}>{p.nombre} — {p.ciudad}</option>)}
                </select>
                {form.proveedor_nombre && <div style={{ fontFamily:G.mono, fontSize:9, color:"#2563EB", marginTop:4 }}>✓ {form.proveedor_nombre}</div>}
              </div>

              {/* Precios */}
              <div style={{ background:"#F0FDF4", border:"1px solid #16A34A33", padding:"14px 16px", marginBottom:16 }}>
                <div style={{ fontFamily:G.sans, fontSize:9, letterSpacing:2, textTransform:"uppercase", color:G.green, marginBottom:12, fontWeight:700 }}>Precio de venta</div>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12 }}>
                  <div>
                    <label style={{ ...lbl, color:G.green }}>Sin IVA (USD) *</label>
                    <input type="number" value={form.precio_venta||""} onChange={e=>{
                      const v = parseFloat(e.target.value) || 0;
                      setForm(p=>({...p, precio_venta:v||"", precio_con_iva:v?(v*1.21).toFixed(2):""}));
                    }} placeholder="0.00" style={{ ...inp, fontSize:16, fontWeight:600 }}/>
                  </div>
                  <div>
                    <label style={lbl}>Con IVA (USD) — editable</label>
                    <input type="number" value={form.precio_con_iva||""} onChange={e=>{
                      const v = parseFloat(e.target.value) || 0;
                      setForm(p=>({...p, precio_con_iva:v||"", precio_venta:v?(v/1.21).toFixed(2):""}));
                    }} placeholder="0.00" style={{ ...inp, fontSize:16, fontWeight:600, color:O }}/>
                    <div style={{ fontFamily:G.mono, fontSize:8, color:G.muted, marginTop:2 }}>Editá acá → calcula sin IVA</div>
                  </div>
                  <div style={{ background:G.s2, padding:"8px 10px", border:`1px solid ${G.border}` }}>
                    <div style={{ fontFamily:G.sans, fontSize:8, letterSpacing:1.5, textTransform:"uppercase", color:G.muted, marginBottom:3, fontWeight:600 }}>En ARS (ref.)</div>
                    <div style={{ fontFamily:G.mono, fontSize:15, color:G.black, fontWeight:700 }}>
                      $ {((parseFloat(form.precio_venta)||0)*(g?.tipo_cambio||1500)).toLocaleString("es-AR",{maximumFractionDigits:0})}
                    </div>
                    <div style={{ fontFamily:G.mono, fontSize:8, color:G.muted }}>TC: {g?.tipo_cambio||1500}</div>
                  </div>
                </div>
              </div>

              {/* Imagen principal */}
              <div style={{ marginBottom:16 }}>
                <ImageUploader value={form.imagen_url||""} onChange={v=>set("imagen_url")(v)} label="Imagen principal"/>
              </div>

              {/* Galería adicional — múltiples fotos */}
              <div style={{ marginBottom:16 }}>
                <div style={{ fontFamily:G.sans, fontSize:9, letterSpacing:2, textTransform:"uppercase", color:G.muted, marginBottom:8, fontWeight:600 }}>
                  Galería adicional ({(form.imagenes||[]).length} fotos)
                </div>
                <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:8 }}>
                  {(form.imagenes||[]).map((url,i) => (
                    <div key={i} style={{ position:"relative" }}>
                      <img src={url} alt="" style={{ width:72, height:72, objectFit:"contain", background:G.s2, border:`1px solid ${G.border}`, borderRadius:2 }}/>
                      <button onClick={()=>setForm(p=>({...p, imagenes:p.imagenes.filter((_,j)=>j!==i)}))}
                        style={{ position:"absolute", top:-6, right:-6, width:18, height:18, background:G.red, color:"#fff", border:"none", borderRadius:"50%", cursor:"pointer", fontSize:10, lineHeight:1, display:"flex", alignItems:"center", justifyContent:"center" }}>×</button>
                    </div>
                  ))}
                  {/* Botón agregar foto */}
                  <ImageUploaderMulti onAdd={url => setForm(p=>({...p, imagenes:[...(p.imagenes||[]), url]}))}/>
                </div>
                <div style={{ fontFamily:G.mono, fontSize:9, color:G.muted }}>Las fotos adicionales aparecen en el slider del detalle del producto</div>
              </div>

              {/* Descripción corta */}
              <div style={{ marginBottom:14 }}>
                <label style={lbl}>Descripción corta (para catálogo PDF)</label>
                <input value={form.descripcion_corta||""} onChange={e=>set("descripcion_corta")(e.target.value)} placeholder="Una línea descriptiva para el catálogo" style={inp}/>
              </div>

              {/* Características (bullets) */}
              <div style={{ marginBottom:14 }}>
                <label style={lbl}>Especificaciones (una por línea)</label>
                <textarea value={form.caracteristicas||""} onChange={e=>set("caracteristicas")(e.target.value)}
                  placeholder={`- Largo: 720mm\n- Ancho: 380mm\n- Color: blanco\n- Material: cerámica vitrificada`}
                  rows={6} style={{ ...inp, resize:"vertical", fontFamily:G.mono, fontSize:12, borderBottom:"none", border:`1.5px solid ${G.border2}`, padding:"8px 10px" }}/>
              </div>

              {/* Publicado */}
              <label style={{ display:"flex", alignItems:"center", gap:10, cursor:"pointer" }}>
                <input type="checkbox" checked={!!form.publicado} onChange={e=>set("publicado")(e.target.checked)}/>
                <span style={{ fontFamily:G.sans, fontSize:12, color:G.off }}>Publicado (visible en el catálogo)</span>
              </label>
            </>
          )}

          {tab === "calc" && (
            <>
              <div style={{ marginBottom:20 }}>
                <div style={{ fontFamily:G.sans, fontSize:9, letterSpacing:2, textTransform:"uppercase", color:G.muted, marginBottom:14, fontWeight:600 }}>Datos de importación</div>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:14 }}>
                  <div>
                    <label style={lbl}>FOB Compra (USD)</label>
                    <input type="number" value={form.fob_compra||""} onChange={e=>set("fob_compra")(e.target.value)} placeholder="0.00" style={inp}/>
                  </div>
                  <div>
                    <label style={lbl}>FOB Declarado (USD)</label>
                    <input type="number" value={form.fob_declarado||""} onChange={e=>set("fob_declarado")(e.target.value)} placeholder="Igual al compra si no especificás" style={inp}/>
                    <div style={{ fontFamily:G.mono, fontSize:8, color:G.muted, marginTop:2 }}>Usar FOB compra si son iguales</div>
                  </div>
                  <div>
                    <label style={lbl}>CBM por unidad (m³)</label>
                    <input type="number" value={form.cbm||""} onChange={e=>set("cbm")(e.target.value)} placeholder={CBM_REFERENCIA[form.rubro]?.cbm||"0.00"} style={inp}/>
                    {CBM_REFERENCIA[form.rubro] && <div style={{ fontFamily:G.mono, fontSize:8, color:G.muted, marginTop:2 }}>Ref: {CBM_REFERENCIA[form.rubro].cbm} {CBM_REFERENCIA[form.rubro].desc}</div>}
                    {/* Alerta si el CBM parece incoherente con el rubro */}
                    {form.cbm && CBM_REFERENCIA[form.rubro] && (() => {
                      const cbmIngresado = parseFloat(form.cbm);
                      const cbmRef = CBM_REFERENCIA[form.rubro]?.cbm;
                      const diferencia = Math.abs(cbmIngresado - cbmRef) / cbmRef;
                      if (diferencia > 0.5) return (
                        <div style={{ fontFamily:G.sans, fontSize:9, color:G.amber, marginTop:4, background:"#FFF7ED", padding:"4px 8px", border:`1px solid ${G.amber}44` }}>
                          ⚠ El CBM {cbmIngresado} difiere mucho del valor de referencia ({cbmRef} para {form.rubro}). Verificá que sea correcto.
                        </div>
                      );
                      return null;
                    })()}
                  </div>
                </div>
              </div>

              {resultado && (
                <div>
                  <div style={{ fontFamily:G.sans, fontSize:9, letterSpacing:2, textTransform:"uppercase", color:G.muted, marginBottom:12, fontWeight:600 }}>Desglose DDP</div>
                  {resultado.desglose.map((d,i) => (
                    <div key={i} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"5px 0", borderBottom:`1px solid ${G.border}` }}>
                      <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                        <span style={{ fontFamily:G.sans, fontSize:11, color:G.muted }}>{d.label}</span>
                        {d.tag && <span style={{ fontFamily:G.mono, fontSize:8, color:G.amber, border:`0.5px solid ${G.amber}`, padding:"1px 4px" }}>{d.tag}</span>}
                      </div>
                      <span style={{ fontFamily:G.mono, fontSize:11, color:G.off }}>{fmtUSD(d.valor)}</span>
                    </div>
                  ))}
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginTop:14 }}>
                    <div style={{ background:"#F0FDF4", padding:"12px 14px", border:`1px solid ${G.green}44` }}>
                      <div style={{ fontFamily:G.sans, fontSize:8, color:G.muted, letterSpacing:1.5, textTransform:"uppercase", marginBottom:3, fontWeight:600 }}>Costo sin IVA (real) — base margen</div>
                      <div style={{ fontFamily:G.mono, fontSize:18, color:G.green, fontWeight:700 }}>{fmtUSD(resultado.costo_puro_real)}</div>
                    </div>
                    <div style={{ background:G.s2, padding:"12px 14px", border:`1px solid ${G.border}` }}>
                      <div style={{ fontFamily:G.sans, fontSize:8, color:G.muted, letterSpacing:1.5, textTransform:"uppercase", marginBottom:3, fontWeight:600 }}>Costo con IVA 21% (real)</div>
                      <div style={{ fontFamily:G.mono, fontSize:18, color:G.black, fontWeight:700 }}>{fmtUSD(resultado.costo_puro_real_con_iva)}</div>
                    </div>
                  </div>
                  {form.precio_venta && resultado && (() => {
                    const pv = parseFloat(form.precio_venta);
                    const cn = resultado.costo_puro_real; // costo real sin IVA/IVA2/IIBB — base de rentabilidad
                    const ganancia = pv - cn;
                    const margen = pv > 0 ? ganancia / pv : 0;
                    const markup = cn > 0 ? ganancia / cn : 0;
                    const precioIVA = pv * 1.21;

                    // Alerta si la ganancia es ridículamente baja (< $3) o negativa
                    const alertaBaja = ganancia < 5;
                    const alertaNegativa = ganancia < 0;

                    return (
                      <div style={{ marginTop:12 }}>
                        {/* Alerta coherencia */}
                        {alertaBaja && (
                          <div style={{ background:alertaNegativa?"#FEF2F2":"#FFF7ED", border:`1.5px solid ${alertaNegativa?G.red:G.amber}`, padding:"10px 14px", marginBottom:12 }}>
                            <div style={{ fontFamily:G.sans, fontSize:12, color:alertaNegativa?G.red:G.amber, fontWeight:700, marginBottom:4 }}>
                              {alertaNegativa ? "⚠ Precio por debajo del costo" : "⚠ Margen muy bajo"}
                            </div>
                            <div style={{ fontFamily:G.sans, fontSize:11, color:G.off, lineHeight:1.5 }}>
                              Con el flete y costos actuales, el costo neto es <strong>{fmtUSD(cn)}</strong>. {alertaNegativa ? "Estás vendiendo a pérdida." : "La ganancia sería de solo " + fmtUSD(ganancia) + " — revisá el precio."}<br/>
                              <span style={{ color:G.muted }}>Precio sugerido con 30% de margen: <strong>{fmtUSD(cn / 0.70)}</strong> · Con 40%: <strong>{fmtUSD(cn / 0.60)}</strong></span>
                            </div>
                          </div>
                        )}

                        {/* Panel de rentabilidad */}
                        <div style={{ background:"#FEF3E8", border:`1px solid ${O}44`, padding:"14px 16px" }}>
                          <div style={{ fontFamily:G.sans, fontSize:9, letterSpacing:2, textTransform:"uppercase", color:G.muted, marginBottom:12, fontWeight:600 }}>Análisis de rentabilidad</div>
                          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr", gap:12 }}>
                            <div>
                              <div style={{ fontFamily:G.sans, fontSize:8, color:G.muted, letterSpacing:1.5, textTransform:"uppercase", marginBottom:4, fontWeight:600 }}>Ganancia</div>
                              <div style={{ fontFamily:G.mono, fontSize:16, color:alertaNegativa?G.red:O, fontWeight:700 }}>{fmtUSD(ganancia)}</div>
                            </div>
                            <div>
                              <div style={{ fontFamily:G.sans, fontSize:8, color:G.muted, letterSpacing:1.5, textTransform:"uppercase", marginBottom:4, fontWeight:600 }}>Margen s/precio</div>
                              <div style={{ fontFamily:G.mono, fontSize:16, color:alertaBaja?G.amber:G.green, fontWeight:700 }}>{(margen*100).toFixed(1)}%</div>
                            </div>
                            <div>
                              <div style={{ fontFamily:G.sans, fontSize:8, color:G.muted, letterSpacing:1.5, textTransform:"uppercase", marginBottom:4, fontWeight:600 }}>Markup s/costo</div>
                              <div style={{ fontFamily:G.mono, fontSize:16, color:alertaBaja?G.amber:G.green, fontWeight:700 }}>{(markup*100).toFixed(1)}%</div>
                            </div>
                            <div>
                              <div style={{ fontFamily:G.sans, fontSize:8, color:G.muted, letterSpacing:1.5, textTransform:"uppercase", marginBottom:4, fontWeight:600 }}>Precio con IVA</div>
                              <div style={{ fontFamily:G.mono, fontSize:16, color:G.black, fontWeight:700 }}>{fmtUSD(precioIVA)}</div>
                            </div>
                          </div>

                          {/* Barra visual del margen */}
                          <div style={{ marginTop:12 }}>
                            <div style={{ display:"flex", justifyContent:"space-between", fontFamily:G.mono, fontSize:9, color:G.muted, marginBottom:4 }}>
                              <span>Costo neto {fmtUSD(cn)} ({(100-margen*100).toFixed(1)}%)</span>
                              <span>Ganancia {fmtUSD(ganancia)} ({(margen*100).toFixed(1)}%)</span>
                            </div>
                            <div style={{ height:8, background:G.border, borderRadius:4, overflow:"hidden" }}>
                              <div style={{ height:"100%", width:`${Math.max(0,Math.min(100,margen*100))}%`, background:alertaNegativa?G.red:alertaBaja?G.amber:G.green, borderRadius:4, transition:"width .3s" }}/>
                            </div>
                          </div>

                          {/* Precios sugeridos */}
                          <div style={{ marginTop:12, display:"flex", gap:8 }}>
                            {[25,30,40].map(pct => {
                              const sug = cn / (1 - pct/100);
                              return (
                                <button key={pct} onClick={()=>setForm(p=>({...p, precio_venta:sug.toFixed(2), precio_con_iva:(sug*1.21).toFixed(2)}))}
                                  style={{ flex:1, padding:"6px 0", background:G.s1, border:`1px solid ${G.border}`, cursor:"pointer", fontFamily:G.sans, fontSize:9, color:G.off, lineHeight:1.4 }}>
                                  <div style={{ fontWeight:700, color:BK }}>{pct}% margen</div>
                                  <div style={{ fontFamily:G.mono, fontSize:10, color:O }}>{fmtUSD(sug)}</div>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                  <div style={{ marginTop:8, background:G.s2, padding:"8px 12px", border:`1px solid ${G.border}`, fontFamily:G.mono, fontSize:10, color:G.muted }}>
                    NCM: {resultado.ncm} · Arancel: {(resultado.arancel*100).toFixed(1)}% · Uso contenedor: {(resultado.pct_contenedor*100).toFixed(2)}%
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer modal */}
        <div style={{ display:"flex", gap:10, padding:"14px 24px", borderTop:`1px solid ${G.border}`, flexWrap:"wrap" }}>
          <button onClick={guardar} disabled={saving}
            style={{ flex:1, padding:"10px 0", background:O, color:"#fff", fontFamily:G.sans, fontSize:9, letterSpacing:2, textTransform:"uppercase", border:"none", cursor:"pointer", fontWeight:700, minWidth:120 }}>
            {saving ? "Guardando..." : esNuevo ? "Crear producto →" : "Guardar cambios →"}
          </button>
          {!esNuevo && (
            <button onClick={duplicar} disabled={saving}
              style={{ padding:"10px 16px", background:"transparent", color:G.muted, fontFamily:G.sans, fontSize:9, letterSpacing:2, textTransform:"uppercase", border:`1.5px solid ${G.border}`, cursor:"pointer" }}>
              Duplicar
            </button>
          )}
          <button onClick={onClose}
            style={{ padding:"10px 16px", background:"transparent", color:G.muted, fontFamily:G.sans, fontSize:9, letterSpacing:2, textTransform:"uppercase", border:`1.5px solid ${G.border}`, cursor:"pointer" }}>
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Modal de recálculo masivo de precios (costo real interno) ────────────────
function ModalRecalculoPrecios({ productos, globales, onClose, onAplicado }) {
  const g = globales || GLOBALES_DEFAULT;
  const [margen, setMargen] = useState(30);
  const [seleccion, setSeleccion] = useState(new Set());
  const [aplicando, setAplicando] = useState(false);
  const [resultado, setResultado] = useState(null);

  const filas = productos
    .filter(p => p.fob_compra && p.cbm)
    .map(p => {
      const r = calcImportacion({
        fob_compra: p.fob_compra, fob_declarado: p.fob_declarado || p.fob_compra,
        cbm: p.cbm, rubro: p.rubro, globales: g,
      });
      const cn = r.costo_puro_real; // costo real sin IVA/IVA2/IIBB — misma base que el panel de rentabilidad
      const nuevoPrecio = precioSugerido(cn, margen / 100);
      const actual = parseFloat(p.precio_venta) || 0;
      const diff = nuevoPrecio - actual;
      return { id: p.id, nombre: p.nombre, sku: p.sku, actual, nuevoPrecio, diff, cn };
    })
    .sort((a, b) => Math.abs(b.diff) - Math.abs(a.diff));

  // Al cambiar el margen, preseleccionar filas con diferencia relevante (> USD 0.50)
  useEffect(() => {
    setSeleccion(new Set(filas.filter(f => Math.abs(f.diff) > 0.5).map(f => f.id)));
  }, [margen, productos]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (id) => setSeleccion(s => {
    const n = new Set(s);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  const toggleTodas = () => setSeleccion(s =>
    s.size === filas.length ? new Set() : new Set(filas.map(f => f.id))
  );

  const aplicar = async () => {
    if (!window.confirm(`¿Actualizar el precio de venta de ${seleccion.size} producto(s)?`)) return;
    setAplicando(true);
    let ok = 0, fail = 0;
    for (const f of filas) {
      if (!seleccion.has(f.id)) continue;
      try {
        await setDoc(doc(db, "productos", f.id), {
          precio_venta:   parseFloat(f.nuevoPrecio.toFixed(2)),
          precio_con_iva: parseFloat((f.nuevoPrecio * 1.21).toFixed(2)),
          updatedAt: serverTimestamp(),
        }, { merge: true });
        ok++;
      } catch (e) { console.error("[RECALCULO] error en", f.id, e); fail++; }
    }
    setAplicando(false);
    setResultado({ ok, fail });
    if (ok > 0) onAplicado();
  };

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(28,25,23,.5)", zIndex:400, display:"flex", alignItems:"flex-start", justifyContent:"center", overflow:"auto", padding:"20px 10px" }}>
      <div style={{ background:G.s1, border:`1.5px solid ${G.border}`, width:"100%", maxWidth:820 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"18px 24px", borderBottom:`1px solid ${G.border}` }}>
          <div style={{ fontFamily:G.sans, fontSize:18, color:G.black, fontWeight:700 }}>Recalcular precios (costo real)</div>
          <button onClick={onClose} style={{ background:"none", border:"none", fontSize:22, color:G.muted, cursor:"pointer" }}>×</button>
        </div>

        <div style={{ padding:"18px 24px" }}>
          <div style={{ fontFamily:G.sans, fontSize:12, color:G.off, marginBottom:14, lineHeight:1.5 }}>
            Recalcula el precio de venta sugerido usando el <strong>costo real</strong> (FOB Compra, no el declarado) con el margen que elijas.
            Es de uso interno — no afecta lo que ve el comprador. Revisá la lista y elegí qué productos actualizar antes de aplicar.
          </div>

          <div style={{ display:"flex", alignItems:"center", gap:14, marginBottom:16, background:G.s2, padding:"10px 14px", border:`1px solid ${G.border}` }}>
            <label style={{ fontFamily:G.sans, fontSize:10, letterSpacing:1.5, textTransform:"uppercase", color:G.muted, fontWeight:600 }}>Margen objetivo</label>
            {[25,30,40].map(pct => (
              <button key={pct} onClick={()=>setMargen(pct)}
                style={{ padding:"6px 14px", background:margen===pct?O:G.s1, color:margen===pct?"#fff":G.off, border:`1px solid ${margen===pct?O:G.border}`, cursor:"pointer", fontFamily:G.sans, fontSize:11, fontWeight:700 }}>
                {pct}%
              </button>
            ))}
            <div style={{ marginLeft:"auto", fontFamily:G.mono, fontSize:10, color:G.muted }}>
              {filas.length} productos con datos DDP · {seleccion.size} seleccionados
            </div>
          </div>

          <div style={{ maxHeight:380, overflow:"auto", border:`1px solid ${G.border}` }}>
            <div style={{ display:"grid", gridTemplateColumns:"30px 1fr 100px 100px 100px", background:G.s2, padding:"8px 12px", gap:8, position:"sticky", top:0 }}>
              <input type="checkbox" checked={seleccion.size===filas.length && filas.length>0} onChange={toggleTodas}/>
              {["Producto","Precio actual","Sugerido nuevo","Diferencia"].map(h=>(
                <div key={h} style={{ fontFamily:G.sans, fontSize:8, letterSpacing:1.5, color:G.muted, textTransform:"uppercase", fontWeight:600 }}>{h}</div>
              ))}
            </div>
            {filas.length === 0 ? (
              <div style={{ padding:24, textAlign:"center", fontFamily:G.sans, fontSize:12, color:G.muted }}>
                Ningún producto tiene FOB Compra y CBM cargados — no hay nada para recalcular.
              </div>
            ) : filas.map(f => (
              <div key={f.id} style={{ display:"grid", gridTemplateColumns:"30px 1fr 100px 100px 100px", padding:"8px 12px", gap:8, borderBottom:`1px solid ${G.border}`, alignItems:"center", background:G.s1 }}>
                <input type="checkbox" checked={seleccion.has(f.id)} onChange={()=>toggle(f.id)}/>
                <div style={{ fontFamily:G.sans, fontSize:11, color:G.black, lineHeight:1.3 }}>{f.nombre}<div style={{ fontFamily:G.mono, fontSize:8, color:G.muted }}>{f.sku||"—"}</div></div>
                <div style={{ fontFamily:G.mono, fontSize:11, color:G.muted }}>{fmtUSD(f.actual)}</div>
                <div style={{ fontFamily:G.mono, fontSize:11, color:G.black, fontWeight:700 }}>{fmtUSD(f.nuevoPrecio)}</div>
                <div style={{ fontFamily:G.mono, fontSize:11, color:f.diff>0?G.green:f.diff<0?G.red:G.muted, fontWeight:700 }}>
                  {f.diff>0?"+":""}{fmtUSD(f.diff)}
                </div>
              </div>
            ))}
          </div>

          {resultado && (
            <div style={{ marginTop:14, background:resultado.fail?"#FEF2F2":"#F0FDF4", border:`1px solid ${resultado.fail?G.red:G.green}44`, padding:"10px 14px", fontFamily:G.sans, fontSize:12, color:G.off }}>
              ✓ {resultado.ok} producto(s) actualizados{resultado.fail?` · ✕ ${resultado.fail} con error (ver consola)`:""}.
            </div>
          )}
        </div>

        <div style={{ display:"flex", gap:10, padding:"14px 24px", borderTop:`1px solid ${G.border}` }}>
          <button onClick={aplicar} disabled={aplicando || seleccion.size===0}
            style={{ flex:1, padding:"10px 0", background:seleccion.size===0?G.dim:O, color:"#fff", fontFamily:G.sans, fontSize:9, letterSpacing:2, textTransform:"uppercase", border:"none", cursor:seleccion.size===0?"default":"pointer", fontWeight:700 }}>
            {aplicando ? "Aplicando..." : `Aplicar a ${seleccion.size} producto(s) →`}
          </button>
          <button onClick={onClose} style={{ padding:"10px 16px", background:"transparent", color:G.muted, fontFamily:G.sans, fontSize:9, letterSpacing:2, textTransform:"uppercase", border:`1.5px solid ${G.border}`, cursor:"pointer" }}>
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}

// ── PANEL PRINCIPAL ───────────────────────────────────────────────────────────
export default function AdminProductos({ globales }) {
  const [productos,   setProductos]   = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [modal,       setModal]       = useState(null); // null | "nuevo" | producto
  const [mostrarRecalculo, setMostrarRecalculo] = useState(false);
  const [filtroRubro, setFiltroRubro] = useState("Todos");
  const [filtroPub,   setFiltroPub]   = useState("Todos");
  const [busqueda,    setBusqueda]    = useState("");

  const cargar = async () => {
    setLoading(true);
    try {
      // Cargar todos los productos sin filtros de Firestore — filtrar en el cliente
      const pSnap = await getDocs(collection(db,"productos"));
      const items = pSnap.docs.map(d=>({id:d.id,...d.data()}));
      items.sort((a,b)=>(a.orden||999)-(b.orden||999)||(a.nombre||"").localeCompare(b.nombre||""));
      console.log("%c[CARGAR] total productos:", "color:#16A34A;font-weight:bold", items.length);
      setProductos(items);
    } catch(e){ console.error("productos:",e); setProductos([]); }
    try {
      const pvSnap = await getDocs(collection(db,"proveedores"));
      setProveedores(pvSnap.docs.map(d=>({id:d.id,...d.data()})));
    } catch(e){ console.error("proveedores:",e); setProveedores([]); }
    setLoading(false);
  };
  useEffect(()=>{ cargar(); },[]);

  const eliminar = async (p) => {
    if (!window.confirm(`¿Eliminar "${p.nombre}"?`)) return;
    console.log("%c[ELIMINAR] borrando", "color:#DC2626;font-weight:bold", { id: p.id, nombre: p.nombre });
    await deleteDoc(doc(db,"productos",p.id));
    console.log("%c[ELIMINAR] borrado OK", "color:#DC2626;font-weight:bold", p.id);
    cargar();
  };

  const togglePublicado = async (p) => {
    await updateDoc(doc(db,"productos",p.id), { publicado:!p.publicado, updatedAt:serverTimestamp() });
    cargar();
  };

  const filtrados = productos.filter(p => {
    const mR = filtroRubro === "Todos" || p.rubro === filtroRubro;
    const mP = filtroPub === "Todos" || (filtroPub==="Publicados"?p.publicado:!p.publicado);
    const mB = !busqueda || p.nombre?.toLowerCase().includes(busqueda.toLowerCase()) || p.sku?.toLowerCase().includes(busqueda.toLowerCase());
    return mR && mP && mB;
  });

  const sel = { background:"#FFFFFF", color:G.black, border:`1px solid ${G.border}`, fontFamily:G.sans, fontSize:12, padding:"6px 10px", outline:"none", WebkitAppearance:"menulist" };

  return (
    <div>
      {/* Encabezado */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
        <div>
          <div style={{ fontFamily:G.sans, fontSize:22, color:G.black, fontWeight:700, marginBottom:3 }}>Productos</div>
          <div style={{ fontFamily:G.mono, fontSize:10, color:G.muted }}>{productos.length} totales · {productos.filter(p=>p.publicado).length} publicados</div>
        </div>
        <div style={{ display:"flex", gap:10 }}>
          <button onClick={()=>setMostrarRecalculo(true)} style={{ padding:"9px 18px", background:"transparent", color:G.off, fontFamily:G.sans, fontSize:9, letterSpacing:2, textTransform:"uppercase", border:`1.5px solid ${G.border}`, cursor:"pointer", fontWeight:700 }}>
            ⚙ Recalcular precios
          </button>
          <button onClick={()=>setModal("nuevo")} style={{ padding:"9px 22px", background:O, color:"#fff", fontFamily:G.sans, fontSize:9, letterSpacing:2, textTransform:"uppercase", border:"none", cursor:"pointer", fontWeight:700 }}>
            + Nuevo producto
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div style={{ display:"flex", gap:10, marginBottom:16, flexWrap:"wrap", alignItems:"center" }}>
        <input value={busqueda} onChange={e=>setBusqueda(e.target.value)} placeholder="Buscar por nombre o SKU..."
          style={{ padding:"6px 10px", border:`1px solid ${G.border}`, fontFamily:G.sans, fontSize:12, color:G.black, background:G.s1, outline:"none", width:220 }}/>
        <select value={filtroRubro} onChange={e=>setFiltroRubro(e.target.value)} style={sel}>
          <option>Todos</option>
          {RUBROS.map(r=><option key={r}>{r}</option>)}
        </select>
        <select value={filtroPub} onChange={e=>setFiltroPub(e.target.value)} style={sel}>
          {["Todos","Publicados","Borradores"].map(o=><option key={o}>{o}</option>)}
        </select>
        <div style={{ fontFamily:G.mono, fontSize:10, color:G.muted, marginLeft:"auto" }}>{filtrados.length} resultados</div>
      </div>

      {/* Tabla */}
      {loading ? (
        <div style={{ padding:40, textAlign:"center", fontFamily:G.sans, fontSize:12, color:G.muted }}>Cargando...</div>
      ) : (
        <div style={{ background:G.border }}>
          <div style={{ display:"grid", gridTemplateColumns:"60px 1fr 80px 80px 100px 80px 120px", background:G.s2, padding:"8px 14px", gap:8 }}>
            {["Foto","Nombre / SKU","Rubro","Precio","Proveedor","Estado","Acciones"].map(h=>(
              <div key={h} style={{ fontFamily:G.sans, fontSize:8, letterSpacing:2, color:G.muted, textTransform:"uppercase", fontWeight:600 }}>{h}</div>
            ))}
          </div>
          {filtrados.length === 0 ? (
            <div style={{ background:G.s1, padding:32, textAlign:"center", fontFamily:G.sans, fontSize:12, color:G.muted }}>
              Sin productos. {busqueda||filtroRubro!=="Todos"?"Probá otro filtro.":"Creá el primero con el botón de arriba."}
            </div>
          ) : filtrados.map(p => (
            <div key={p.id} style={{ display:"grid", gridTemplateColumns:"60px 1fr 80px 80px 100px 80px 120px", background:G.s1, padding:"10px 14px", gap:8, borderBottom:`1px solid ${G.border}`, alignItems:"center" }}>
              <div>
                {p.imagen_url
                  ? <img src={p.imagen_url} alt="" style={{ width:50, height:50, objectFit:"contain", background:G.s2, border:`1px solid ${G.border}` }}/>
                  : <div style={{ width:50, height:50, background:G.s2, border:`1px solid ${G.border}`, display:"flex", alignItems:"center", justifyContent:"center", fontFamily:G.mono, fontSize:8, color:G.muted }}>—</div>
                }
              </div>
              <div>
                <div style={{ fontFamily:G.sans, fontSize:13, color:G.black, fontWeight:600, lineHeight:1.3 }}>{p.nombre}</div>
                <div style={{ fontFamily:G.mono, fontSize:9, color:G.muted, marginTop:1 }}>{p.sku||"—"} · Orden: {p.orden||"—"}</div>
                <div style={{ fontFamily:G.mono, fontSize:8, color:G.dim, marginTop:1 }} title="ID de Firestore — usalo para confirmar si dos filas son en realidad el mismo documento">ID: {p.id}</div>
              </div>
              <div style={{ fontFamily:G.sans, fontSize:10, color:O }}>{p.rubro}</div>
              <div>
                {p.precio_venta
                  ? <div style={{ fontFamily:G.mono, fontSize:11, color:G.black }}>USD {Number(p.precio_venta).toFixed(2)}</div>
                  : <div style={{ fontFamily:G.sans, fontSize:9, color:G.muted }}>—</div>
                }
              </div>
              <div style={{ fontFamily:G.sans, fontSize:10, color:"#2563EB" }}>
                {p.proveedor_nombre || <span style={{ color:G.muted }}>—</span>}
              </div>
              <div>
                <span style={{ fontFamily:G.mono, fontSize:8, color:p.publicado?G.green:G.muted, border:`1px solid ${p.publicado?G.green:G.muted}`, padding:"2px 6px", letterSpacing:1, textTransform:"uppercase" }}>
                  {p.publicado?"Activo":"Borrador"}
                </span>
              </div>
              <div style={{ display:"flex", gap:6 }}>
                <button onClick={()=>setModal(p)} style={{ fontFamily:G.mono, fontSize:14, color:G.blue, background:"none", border:"none", cursor:"pointer" }}>✎</button>
                <button onClick={()=>togglePublicado(p)} style={{ fontFamily:G.mono, fontSize:14, color:p.publicado?G.green:G.muted, background:"none", border:"none", cursor:"pointer" }}>◉</button>
                <button onClick={()=>eliminar(p)} style={{ fontFamily:G.mono, fontSize:14, color:G.red, background:"none", border:"none", cursor:"pointer" }}>✕</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <ModalProducto
          producto={modal==="nuevo"?null:modal}
          globales={globales}
          proveedores={proveedores}
          onGuardar={()=>{setModal(null);cargar();}}
          onClose={()=>setModal(null)}
        />
      )}

      {mostrarRecalculo && (
        <ModalRecalculoPrecios
          productos={productos}
          globales={globales}
          onClose={()=>setMostrarRecalculo(false)}
          onAplicado={cargar}
        />
      )}
    </div>
  );
}
